<template>
    <div>
        <oss-dialog v-model="dialog" :title="title" class="px-2" :minWidth="'60vw'">
            <v-form v-model="isValid">
                <v-select-filled
                    v-model="mailAddressSender"
                    :items="credentialsEmailAddresses"
                    item-value="item"
                    :rules="[rules.notEmpty]"
                    label="Mail qui va envoyer la facture"
                    class="px-2 mb-0 mt-1"
                    append-icon="$add"
                    @click:append="accountingConfigAPIDialog = true"
                    :item-title="(item) => item.email"
                />
                <v-combobox-filled
                    v-if="!sendMultipleMails"
                    v-model="mailAddressesDestination"
                    :items="thirdCompanyData.users"
                    :rules="[rules.onlyEmails]"
                    label="&#xc0;"
                    class="px-2 pt-2"
                    item-key="email"
                    multiple
                    chips
                    closable-chips
                    density="compact"
                    append-icon="$add"
                    @click:append="thirdCompanyUsersDialog = true"
                    :item-title="(item) => displayTPCData(item)"
                />

                <v-select-filled
                    v-if="mailTemplates?.length > 1"
                    label="Modèle de mail"
                    v-model="selectedTemplateName"
                    :items="mailTemplates"
                    item-title="title"
                    class="px-2 pt-2"
                    @update:modelValue="loadTemplateContent"
                    data-cy="MailerDialog-mailTemplate"
                >
                </v-select-filled>
                <v-text-field-filled
                    v-model="subjectMailToSend"
                    :rules="[rules.notEmpty]"
                    label="Objet"
                    class="px-2 pt-2"
                    single-line
                />
            </v-form>
            <div class="px-2 py-0 pt-2">
                <editor v-model="textMail" />
            </div>

            <template v-slot:actions>
                <v-btn-primary
                    :disabled="!isValid"
                    rounded
                    block
                    :loading="loading"
                    @click="sendMails()"
                    data-cy="MailerDialogue-valid"
                    size="x-large"
                    >Envoyer</v-btn-primary
                >
            </template>

            <v-snackbar v-model="mailSent">
                Mail envoyé
                <template v-slot:actions>
                    <v-btn color="green" @click="mailSent = false" variant="text"> Close </v-btn>
                </template>
            </v-snackbar>
        </oss-dialog>
        <AccountingConfigAPI
            v-model="accountingConfigAPIDialog"
            :config="accountingConfig.config"
            :profileId="accountingConfig.profileId"
            :companyId="accountingConfig.companyId"
            :mailUsage="'sendingMail'"
            @onUpdate="refresh()"
        />

        <InvoiceThirdCompanyUsersDialog
            v-if="thirdCompanyUsersDialog"
            v-model="thirdCompanyUsersDialog"
            :companyId="companyId"
            :thirdPartyCompanyId="thirdPartyCompanyId"
            :onlyToCreate="true"
            @valid="refresh()"
        />
    </div>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";

import AccountingConfigAPI from "../admin/AccountingConfigAPI.vue";
import InvoiceThirdCompanyUsersDialog from "../invoice/InvoiceThirdCompanyUsersDialog.vue";

import THIRD_PARTY_COMPANY_DATA from "../../graphql/invoice/ThirdPartyCompanyData.gql";
import SEND_MULTIPLE_DOCUMENTS_BY_MAILS from "../../graphql/invoice/MultipleDocumentsMailer.gql";
import INVOICE_MAILER from "../../graphql/invoice/InvoiceMailer.gql";
import gql from "graphql-tag";
import Editor from "../../components/editorMenu/Editor.vue";

import { emailPattern } from "../../services/emailServices";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import moment from "moment-timezone";
import { Component, Vue, Watch, toNative } from "vue-facing-decorator";
import { Prop, Model } from "vue-facing-decorator";
import { MailTemplate } from "@/store/store";
import { User } from "../../../../types/types/indexTypes";
import { formatInvoiceText } from "./mail.helper";

@Component({
    components: {
        AccountingConfigAPI,
        InvoiceThirdCompanyUsersDialog,
        Editor,
    },
    emits: ["mailSent"],
})
export class MailerDialog extends Vue {
    @Model({ default: null }) value: boolean;
    @Prop({ default: "Envoi de mail" }) title: string;
    @Prop({ default: "UNKNOWN" }) mailType: string;
    @Prop({ default: "" }) defaultSubject: string;
    @Prop({ default: "" }) defaultText: string;
    @Prop({ default: false }) sendMultipleMails; // receivers emails already saved into invoices/documents data, not used for the moment
    @Prop({ default: null }) companyId: string;
    @Prop({ default: null }) thirdPartyCompanyId: string;
    @Prop({ default: null }) defaultReceivers: Array<any>;
    @Prop({ default: null }) listDocuments: any;
    @Prop({ default: null }) invoice: any;
    @Prop({ default: false }) withAttachment: boolean;
    @Prop({ default: null }) budgetInsightOptions: any;
    @Prop({ default: null }) invoiceData: any;

    isValid = false;
    subjectMailToSend = "";
    textMail = "";
    mailAddressSender = "";
    mailAddressesDestination = [];

    loading = false;
    mailSent = false;

    addPaymentURL = false;

    accountingConfigAPIDialog = false;
    thirdCompanyUsersDialog = false;

    // create a local variable to avoid modifying the store directly
    private selectedTemplateNameLocal: string = "";

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
    }

    get rules() {
        return {
            isEmail: (value) => {
                return emailPattern.test(value) || "E-mail invalide";
            },
            onlyEmails: (value) => {
                if (!value || !value.length) {
                    return "A remplir";
                }
                for (let v of value) {
                    if (v.email) {
                        v = v.email;
                    }
                    if (this.rules.isEmail(v) === "E-mail invalide") {
                        return "Seuls des emails sont acceptés";
                    }
                }
                return true;
            },
            notEmpty: (value) => {
                return !!value || "A remplir";
            },
        };
    }

    get accountingConfig() {
        return {
            profileId: this.$store.state.user.company.profile.id,
            companyId: this.$store.state.user.company.id,
            config: {
                sendingMailSMTPConfig: {
                    users: this.credentialsEmailAddresses.filter(
                        (e) => e.email !== "Utiliser l'adresse mail de bobbee",
                    ),
                },
            },
        };
    }

    // Fetch mail templates
    get mailTemplates(): MailTemplate[] {
        return this.invoiceData?.mail?.invoice?.templates || this.$store.state.invoiceMailTemplates;
    }
    // Fetch selectedTemplateName from the store
    get selectedTemplateNameFromStore(): string {
        return (
            this.invoiceData?.mail?.invoice?.selectedTemplateName ||
            this.$store.state.selectedInvoiceMailTemplateName
        );
    }

    // Get selectedTemplateName
    get selectedTemplateName(): string {
        return this.selectedTemplateNameLocal;
    }
    // Update selectedTemplateName
    set selectedTemplateName(value: string) {
        this.selectedTemplateNameLocal = value;
    }

    // Get user from store
    get userFromStore(): Partial<User> {
        return this.$store.state.user;
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    beforeMount() {
        if (this.defaultReceivers?.length) {
            if (this.defaultReceivers[0]?.email) {
                // voir avec Romain si bonne méthode
                this.mailAddressesDestination = this.defaultReceivers;
            }
        }
        this.subjectMailToSend = this.defaultSubject;
        this.textMail = this.defaultText;
    }

    // Watcher: Update selectedTemplateNameLocal when selectedTemplateNameFromStore changes
    @Watch("selectedTemplateNameFromStore", { immediate: true })
    onSelectedTemplateNameChange(newValue: string) {
        this.selectedTemplateNameLocal = newValue;
    }

    @Apollo({
        query: gql`
            query CompanyQuery($companyId: ID!) {
                company(where: { id: $companyId }) {
                    id
                    name
                    profile {
                        id
                        sendingMailSMTPConfig
                    }
                }
            }
        `,
        variables() {
            return {
                companyId: this.$store.state.user.company.id,
            };
        },
        skip() {
            return !this.$store.state.user.company.id;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (
                    data &&
                    data.company &&
                    data.company.profile &&
                    data.company.profile.sendingMailSMTPConfig
                ) {
                    this.credentialsEmailAddresses = JSON.parse(
                        JSON.stringify(data.company.profile.sendingMailSMTPConfig.users),
                    );
                    this.credentialsEmailAddresses.push({
                        email: "Utiliser l'adresse mail de bobbee",
                    });
                    if (this.credentialsEmailAddresses.length) {
                        this.mailAddressSender = this.credentialsEmailAddresses[0].email;
                    }
                } else {
                    this.credentialsEmailAddresses = [
                        {
                            email: "Utiliser l'adresse mail de bobbee",
                        },
                    ];
                    this.mailAddressSender = "Utiliser l'adresse mail de bobbee";
                }
            }
        },
    })
    credentialsEmailAddresses: Array<any> = [];

    @Apollo({
        query() {
            return THIRD_PARTY_COMPANY_DATA;
        },
        variables() {
            return {
                thirdPartyCompanyId: this.thirdPartyCompanyId,
            };
        },
        skip() {
            return !this.companyId || !this.thirdPartyCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.company?.thirdPartyCompanyData) {
                    const thirdCompanyData = data.company.thirdPartyCompanyData;
                    if (thirdCompanyData.users) {
                        this.users = thirdCompanyData.users;
                    } else {
                        thirdCompanyData.users = [];
                    }
                    this.thirdCompanyData = thirdCompanyData;
                } else {
                    this.thirdCompanyData = { users: [] };
                }

                // if (data && data.thirdPartyCompanyDatas && data.thirdPartyCompanyDatas.length && data.thirdPartyCompanyDatas[0].data) {
                //     const result = []
                //     for(const tpcDataUser of data.thirdPartyCompanyDatas[0].data.users) {
                //         if(tpcDataUser.email) {
                //             const email = tpcDataUser.email
                //             let firstname; let lastname
                //             if(tpcDataUser.firstname && tpcDataUser.lastname) {
                //                 firstname = tpcDataUser.firstname
                //                 lastname = tpcDataUser.lastname
                //             }
                //             result.push({ email: email, firstname: firstname, lastname: lastname })
                //         }
                //     }
                //     this.contactsInDatabase = result
                // } else {
                //     this.contactsInDatabase = []
                // }
            }
        },
    })
    thirdCompanyData: any = {
        users: [],
    };

    // contactsInDatabase: Array<any> = []

    // Method: Load the content of the selected template
    loadTemplateContent(templateName: string) {
        const selectedTemplate = this.mailTemplates.find(
            (template) => template.value === templateName,
        );
        if (!selectedTemplate) {
            console.error("Template not found:", templateName);
            return;
        }

        // Update default mail content
        this.subjectMailToSend = formatInvoiceText(
            this.invoice[0],
            this.userFromStore,
            selectedTemplate.header,
        );
        this.textMail = formatInvoiceText(
            this.invoice[0],
            this.userFromStore,
            selectedTemplate.body + "\n\n" + selectedTemplate.signature,
        );
    }

    async sendMails() {
        this.loading = true;
        switch (this.mailType) {
            case "SENT_INVOICE":
                await this.sendMailInvoice();
                break;
            case "SENT_GROUPED_INVOICES":
                break;
            default:
                await this.sendMailDocuments();
                break;
        }
        this.loading = false;
        this.dialog = false;
    }

    async sendMailDocuments() {
        console.log(this.listDocuments);
        const info = await this.$apollo.mutate({
            mutation: SEND_MULTIPLE_DOCUMENTS_BY_MAILS,
            variables: {
                listDocuments: this.listDocuments,
                userId: this.$store.state.user.id,
                profileCompanyId: this.$store.state.user.company.profile.id,
                type: this.mailType,
                subject: this.subjectMailToSend,
                credentialsSender:
                    this.mailAddressSender === "Utiliser l'adresse mail de bobbee"
                        ? "useBobbeeEmailAddress"
                        : this.mailAddressSender,
                textMail: this.textMail,
                receiverEmailAddress: this.convertAndJoin(this.mailAddressesDestination),
                withAttachment: this.withAttachment,
                companyId: this.companyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId,
                budgetInsightOptions: this.budgetInsightOptions,
            },
        });
        if (info.data.multipleDocumentsMailer.info) {
            this.$emit("mailSent");
        }
    }

    async sendMailInvoice() {
        const invoices = this.invoice.map((invoice) => {
            return invoice.id;
        });
        const info = await this.$apollo.mutate({
            mutation: INVOICE_MAILER,
            variables: {
                invoiceId: invoices,
                userId: this.$store.state.user.id,
                profileCompanyId: this.$store.state.user.company.profile.id,
                subject: this.subjectMailToSend,
                credentialsSender:
                    this.mailAddressSender === "Utiliser l'adresse mail de bobbee"
                        ? "useBobbeeEmailAddress"
                        : this.mailAddressSender,
                textMail: this.textMail,
                receiverEmailAddress: this.convertAndJoin(this.mailAddressesDestination),
                withAttachment: this.withAttachment,
                companyId: this.companyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId,
                budgetInsightOptions: this.budgetInsightOptions,
            },
        });
        if (info.data.invoiceMailer.info) {
            this.dialog = false;
            this.mailSent = true;
            this.reinitialiseComponent();
        }
    }

    displayTPCData(item) {
        if (item.firstname && item.lastname) {
            return "<" + item.firstname + " " + item.lastname + "> " + item.email;
        } else {
            return item.email;
        }
    }

    convertAndJoin(emails) {
        const result = [];
        for (const email of emails) {
            if (email.email) {
                result.push(email.email);
            } else {
                result.push(email);
            }
        }
        return result.join();
    }

    textForAmount(stillOwe, debit, credit) {
        if (stillOwe !== debit - credit) {
            return (
                " montant initial de " +
                newCommonFormatCurrency(Math.abs(debit - credit)) +
                " (avec un reste à payer de " +
                newCommonFormatCurrency(Math.abs(stillOwe)) +
                ")"
            );
        } else {
            return " montant de " + newCommonFormatCurrency(Math.abs(stillOwe));
        }
    }

    formatDate(date) {
        if (!date) return "";
        moment.locale("fr");
        return moment(date).format("DD/MM/YYYY");
    }

    reinitialiseComponent() {
        this.isValid = false;
        this.subjectMailToSend = "";
        this.textMail = "";
        this.mailAddressSender = "";
        this.mailAddressesDestination = [];
        this.loading = false;
        this.accountingConfigAPIDialog = false;
        this.thirdCompanyUsersDialog = false;
    }

    refresh() {
        if (this.$apollo.queries.credentialsEmailAddresses) {
            this.$apollo.queries.credentialsEmailAddresses.refetch();
        }
        if (this.$apollo.queries.thirdCompanyData) {
            this.$apollo.queries.thirdCompanyData.refetch();
            this.thirdCompanyUsersDialog = false;
        }
    }
}

export default toNative(MailerDialog);
</script>
